/**
 * Tenant branding loader.
 *
 * Fetches `/company-settings` after login and applies `primary_color` /
 * `accent_color` as CSS variables on `:root` so any Tailwind/inline use of
 * `var(--cm-primary)` automatically respects each tenant's brand.
 *
 * Also exposes the resolved branding via React context for components that
 * want to read `name`, `logo_path`, `app_subtitle`, etc.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api } from "../lib/api";
import { useAuth } from "../auth/AuthContext";

const DEFAULTS = {
  name: "Clean Manager USA",
  app_subtitle: "Operações de Limpeza",
  logo_path: "",
  primary_color: "#2563EB",
  accent_color: "#0EA5E9",
};

const BrandingCtx = createContext({ branding: DEFAULTS, refresh: () => {} });

function applyCssVars(b) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.setProperty("--cm-primary", b.primary_color || DEFAULTS.primary_color);
  root.style.setProperty("--cm-accent", b.accent_color || DEFAULTS.accent_color);
}

export function BrandingProvider({ children }) {
  const { user } = useAuth();
  const [branding, setBranding] = useState(DEFAULTS);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/company-settings");
      const merged = { ...DEFAULTS, ...data };
      setBranding(merged);
      applyCssVars(merged);
    } catch {
      // not authenticated yet or endpoint failed — keep defaults
    }
  }, []);

  // Re-fetch whenever the user changes (login / logout / switch tenant).
  useEffect(() => {
    if (user && user.id) {
      refresh();
    } else {
      setBranding(DEFAULTS);
      applyCssVars(DEFAULTS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user && user.id, user && user.tenant_id]);

  const value = useMemo(() => ({ branding, refresh }), [branding, refresh]);

  return <BrandingCtx.Provider value={value}>{children}</BrandingCtx.Provider>;
}

export function useBranding() {
  return useContext(BrandingCtx);
}
